import { ESdChangeReason } from "@kuika/kuika-cl-model";

/**
 * What a step's screen still owes the server.
 *
 * ## Why a ledger and not "send the tree"
 *
 * Apply Screens rewrites the managed part of a task screen. If the browser also sent the WHOLE
 * tree, it would overwrite whatever else changed on that screen meanwhile — another session's
 * canvas edit, a collab participant's work. So composition travels as OPERATIONS: insert this
 * subtree under that anchor, configure that property, delete that component. The server applies
 * them to the tree it holds.
 *
 * ## The collapse rules are the correctness
 *
 * A ledger that merely appends would ask the server to do impossible things:
 *
 *   - Deleting a component the user JUST placed must not send a delete — the server has never seen
 *     that component; it must send nothing at all. The insert is withdrawn instead.
 *   - Deleting anything withdraws the configures recorded for the ids that went with it, including
 *     the ones inside its subtree. A configure addressed to a component that no longer exists is
 *     an error the server would have to guess about.
 *   - Configuring the same property twice is one operation: the tree carries the final value, and
 *     the operation is only an address.
 *
 * The ledger is pure and holds no tree. The caller has the tree, so a delete reports which ids it
 * removed and the ledger reasons about that list.
 */

export interface ICompositionInsert {
  op: "insert";
  /** The component the subtree was placed into. */
  anchorKuikaId: number;
  /** The placed component's id, as it sits in the tree the browser holds. */
  kuikaId: number;
  componentName: string;
}

export interface ICompositionConfigure {
  op: "configure";
  kuikaId: number;
  propertyName: string;
}

export interface ICompositionDelete {
  op: "delete";
  kuikaId: number;
}

/**
 * This component's EVENT WIRING changed — an action was added, edited, reordered or removed.
 *
 * Separate from `configure` because it addresses the component rather than one named property: an
 * action lives in the component's event list, carries its own parameters and conditions, and the
 * editors that author it write the whole thing. The operation says WHICH component to take the
 * wiring from; the tree the browser holds is where it is taken from.
 */
export interface ICompositionWire {
  op: "wire";
  kuikaId: number;
}

/**
 * This host's CHILD ORDER changed — a table's columns were moved, a menu's items resorted.
 *
 * Addressed to the HOST rather than to the child that moved, and recorded once however many moves
 * the user made, because the operation carries no diff: the browser's tree holds the final order
 * and the payload reads it from there. Per-child "move X in front of Y" operations were the
 * alternative and they do not compose — replaying two of them against the server's copy can land an
 * order neither side ever had.
 */
export interface ICompositionReorder {
  op: "reorder";
  /** The host whose children were reordered. */
  kuikaId: number;
}

export type CompositionOperation =
  | ICompositionInsert
  | ICompositionConfigure
  | ICompositionDelete
  | ICompositionWire
  | ICompositionReorder;

/** Ids the browser placed itself: the server has never seen them. */
function locallyInsertedIds(operations: readonly CompositionOperation[]): Set<number> {
  const ids: Set<number> = new Set<number>();
  operations.forEach((operation: CompositionOperation): void => {
    if (operation.op === "insert") ids.add(operation.kuikaId);
  });
  return ids;
}

/** Record a placement. */
export function recordInsert(
  operations: readonly CompositionOperation[],
  insert: Omit<ICompositionInsert, "op">
): CompositionOperation[] {
  return [...operations, { op: "insert", ...insert }];
}

/**
 * Record a property change.
 *
 * Skipped entirely for a component the browser placed itself: its whole subtree is sent by the
 * insert, with the property already on it. Recording one would ask the server to configure a
 * component in the same breath as creating it.
 */
export function recordConfigure(
  operations: readonly CompositionOperation[],
  configure: Omit<ICompositionConfigure, "op">
): CompositionOperation[] {
  if (locallyInsertedIds(operations).has(configure.kuikaId)) return [...operations];
  const alreadyRecorded: boolean = operations.some(
    (operation: CompositionOperation): boolean =>
      operation.op === "configure" &&
      operation.kuikaId === configure.kuikaId &&
      operation.propertyName === configure.propertyName
  );
  if (alreadyRecorded) return [...operations];
  return [...operations, { op: "configure", ...configure }];
}

/**
 * Record that a component's event wiring changed.
 *
 * Skipped for a component this session placed — its insert carries the events with it — and
 * recorded once per component however many actions were touched, because the operation is an
 * address, not a diff.
 */
/**
 * The platform's own reasons for an ACTION write, borrowed from the event panel's re-render rule
 * (`EventPanel.checkNeedRender`) so the two agree on what counts as action authoring.
 *
 * Used to tell an action write apart from a placement (`DragDropDi`) or a property change
 * (`ChangePropertyValue`) when the only signal available is that the screen changed — the
 * platform's action editors report through the tree, not through a callback.
 */
const ACTION_WRITE_REASONS: ReadonlySet<ESdChangeReason> = new Set<ESdChangeReason>([
  ESdChangeReason.DiActionUpdated,
  ESdChangeReason.ActionParameterFixedValueChanged,
  ESdChangeReason.ConfirmationMessageChanged,
  ESdChangeReason.RecurringIntervalChange,
  ESdChangeReason.StartingScreenIdChanged,
  ESdChangeReason.WFStepMappingChange
]);

/** Whether a screen write was an action-authoring write. */
export function isActionWriteReason(reason: ESdChangeReason | undefined): boolean {
  if (reason === undefined) return false;
  return ACTION_WRITE_REASONS.has(reason);
}

export function recordWire(operations: readonly CompositionOperation[], kuikaId: number): CompositionOperation[] {
  if (locallyInsertedIds(operations).has(kuikaId)) return [...operations];
  const alreadyRecorded: boolean = operations.some(
    (operation: CompositionOperation): boolean => operation.op === "wire" && operation.kuikaId === kuikaId
  );
  if (alreadyRecorded) return [...operations];
  return [...operations, { op: "wire", kuikaId }];
}

export interface IRecordReorderInput {
  /** The host whose children moved. */
  kuikaId: number;
}

/**
 * Record that a host's child order changed.
 *
 * Skipped for a host this session placed — its insert carries the whole subtree, children in their
 * final order — and recorded once per host, because the operation is an address and the tree is
 * where the order is read from.
 */
export function recordReorder(
  operations: readonly CompositionOperation[],
  input: IRecordReorderInput
): CompositionOperation[] {
  if (locallyInsertedIds(operations).has(input.kuikaId)) return [...operations];
  const alreadyRecorded: boolean = operations.some(
    (operation: CompositionOperation): boolean => operation.op === "reorder" && operation.kuikaId === input.kuikaId
  );
  if (alreadyRecorded) return [...operations];
  return [...operations, { op: "reorder", kuikaId: input.kuikaId }];
}

export interface IRecordDeleteInput {
  /** The component that was removed. */
  kuikaId: number;
  /** Every id the removal took off the screen, the component itself included. */
  removedKuikaIds: readonly number[];
}

/**
 * Record a removal, withdrawing everything the removal made moot.
 *
 * When the removed component was placed in this same session, NO delete is recorded — the server
 * never learned about it, so there is nothing to delete there.
 */
export function recordDelete(
  operations: readonly CompositionOperation[],
  input: IRecordDeleteInput
): CompositionOperation[] {
  const removed: Set<number> = new Set<number>(input.removedKuikaIds);
  removed.add(input.kuikaId);
  const wasLocallyInserted: boolean = locallyInsertedIds(operations).has(input.kuikaId);

  const kept: CompositionOperation[] = operations.filter((operation: CompositionOperation): boolean => {
    if (operation.op === "insert") return !removed.has(operation.kuikaId);
    if (operation.op === "configure") return !removed.has(operation.kuikaId);
    // Wiring of something that is gone is nothing to replay.
    if (operation.op === "wire") return !removed.has(operation.kuikaId);
    // Nor is the child order of a host that is gone.
    if (operation.op === "reorder") return !removed.has(operation.kuikaId);
    // A delete of something inside what we just removed is covered by the outer delete.
    return !removed.has(operation.kuikaId) || operation.kuikaId === input.kuikaId;
  });

  if (wasLocallyInserted) return kept;
  const alreadyDeleted: boolean = kept.some(
    (operation: CompositionOperation): boolean => operation.op === "delete" && operation.kuikaId === input.kuikaId
  );
  if (alreadyDeleted) return kept;
  return [...kept, { op: "delete", kuikaId: input.kuikaId }];
}

/** How many operations this step still owes the server — what the Apply counter adds up. */
export function pendingOperationCount(operations: readonly CompositionOperation[] | undefined): number {
  return operations?.length ?? 0;
}

/** Whether this id was placed in this session (so the UI may offer to take it back out). */
export function isLocallyInserted(
  operations: readonly CompositionOperation[] | undefined,
  kuikaId: number | null | undefined
): boolean {
  if (operations === undefined || kuikaId === undefined || kuikaId === null) return false;
  return locallyInsertedIds(operations).has(kuikaId);
}
